// Library methods
import React, { useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

// Components
import ProgressBar from "../../ProgressBar";

// Utilities
import {
  getDeviceStagesTimelineText,
  DeviceStagesTimeline,
} from "../../../../utils/signalRHelper";
import { getColorVisionTime } from "../../../../utils/examReportsHelper";
import { useLiveUpdateStatusContext } from "../../../../contexts/LiveUpdateStatusContext";

const LiveUpdateStepper = () => {
  // translations
  const { t } = useTranslation();

  const {
    examStatus,
    examReportLive,
    activeStep,
    possibleSteps,
    percentage,
    isConfirmation,
    isNotStarted,
    isRightCompleted,
    isTerminated,
    isVisualField,
    isEsterman,
    actions,
  } = useLiveUpdateStatusContext();
  const { setPercentage, setExamReportLive } = actions;

  const elapsedTime = useMemo(() => {
    if (examReportLive?.timing?.elapsed)
      return getColorVisionTime(examReportLive.timing.elapsed);
    return "00:00";
  }, [examReportLive?.timing?.elapsed]);

  const allExamSteps = useMemo(
    () =>
      possibleSteps.map((stage) => {
        // if the exam is at the Exam stage, we also display the status (as it may be In Progress or Paused)
        const isExamOrTutorialStage =
          (stage === DeviceStagesTimeline.Exam ||
            stage === DeviceStagesTimeline.Tutorial) &&
          activeStep.value === stage;
        return {
          title: getDeviceStagesTimelineText(
            stage,
            isExamOrTutorialStage,
            examStatus?.extraWord ?? examStatus?.status,
            t
          ),
        };
      }),
    [activeStep, examStatus, possibleSteps, t]
  );

  useEffect(() => {
    setPercentage(
      Math.floor(
        examReportLive?.stats?.progress
          ? examReportLive.stats.progress * 100
          : examReportLive?.Progress
            ? examReportLive?.Progress * 100
            : 0
      )
    );
  }, [
    setPercentage,
    examReportLive?.stats?.progress,
    examReportLive?.Progress,
  ]);

  useEffect(() => {
    if (isConfirmation || isNotStarted || isRightCompleted || isTerminated) {
      setPercentage(0);
      setExamReportLive(undefined);
    }
  }, [
    isConfirmation,
    isRightCompleted,
    isNotStarted,
    isTerminated,
    setPercentage,
    setExamReportLive,
  ]);

  return (
    <Box display="flex" flexDirection="column" gap="20px">
      {!isVisualField && !isEsterman && (
        <Box display="flex" gap="4px">
          <Typography fontWeight={600}>{t("word_elapsed_time")}:</Typography>
          <Typography>{elapsedTime}</Typography>
        </Box>
      )}
      <Grid
        display="flex"
        flexWrap="wrap"
        alignContent="center"
        justifyContent={{ xs: "space-around", sm: "space-between" }}
        padding="16px"
        backgroundColor="#5B6D7A10"
        borderRadius="8px"
        gap="20px"
      >
        {allExamSteps.map((step, index) => {
          return (
            <Box
              key={step.title}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              gap="8px"
            >
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="40px"
                width="40px"
                backgroundColor={
                  activeStep.index === index
                    ? "#E2772E"
                    : index < activeStep.index
                      ? "#202338"
                      : "#FFF"
                }
                border={index > activeStep.index ? "1px solid #202338" : "none"}
                color={index > activeStep.index ? "#202338" : "#FFF"}
                borderRadius="100%"
              >
                <Typography fontSize="16px" fontWeight={600}>
                  {index + 1}
                </Typography>
              </Box>
              <Typography
                color={activeStep.index === index ? "#E2772E" : "#202338"}
              >
                {step.title}
              </Typography>
            </Box>
          );
        })}
      </Grid>
      <ProgressBar value={percentage} />
    </Box>
  );
};

export default LiveUpdateStepper;
